import { isArray, mergeWith } from "lodash";
import { RootState } from "src/store";
import {
    disableTwoFactor as apiDisableTwoFactor,
    enableTwoFactor as apiEnableTwoFactor,
    getProfile as apiGetProfile,
    updateProfile as apiUpdateProfile,
} from "src/ui/containers/account/api";
import { PartialDeep } from "type-fest";

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

type AccountState = {
    profile: API.Profile | null;
};

export const fetchProfile = createAsyncThunk("profile/fetch", apiGetProfile);
export const updateProfile = createAsyncThunk("profile/update", apiUpdateProfile);
export const enableTwoFactor = createAsyncThunk("profile/enable/2fa", apiEnableTwoFactor);
export const disableTwoFactor = createAsyncThunk("profile/disable/2fa", apiDisableTwoFactor);

const initialState: AccountState = {
    profile: null,
};

const initialWithLastPersistedState: AccountState = mergeWith(
    initialState,
    {
        // override anything from "initial-state" & "last-persisted-state" here
    },
    (obj: PartialDeep<AccountState>, src: PartialDeep<AccountState>) => {
        if (isArray(obj)) {
            return src;
        }
    },
);

const slice = createSlice({
    name: "account",
    initialState: initialWithLastPersistedState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchProfile.fulfilled, (state: AccountState, { payload }) => {
            if (payload) {
                state.profile = payload;
            }
        });
        // `updateProfile` reports a rejected save by resolving with `success: false` rather than throwing, so
        // this case also runs on failure. Without the check the unsaved form values overwrote the stored profile
        // and the header showed a value the backend had refused.
        builder.addCase(updateProfile.fulfilled, (state: AccountState, { payload, meta }) => {
            if (payload?.success) {
                state.profile = meta.arg;
            }
        });
        builder.addCase(enableTwoFactor.fulfilled, (state) => {
            if (state.profile) {
                state.profile.twoFactorEnabled = true;
            }
        });
        builder.addCase(disableTwoFactor.fulfilled, (state) => {
            if (state.profile) {
                state.profile.twoFactorEnabled = false;
            }
        });
    },
});

export const selectProfile = (state: RootState): API.Profile | null => state.account.profile;

export default slice.reducer;
